export interface RetryOptions {
    attempts?: number;
    baseDelayMs?: number;
    maxDelayMs?: number;
    factor?: number;
    shouldRetry?: (error: unknown, attempt: number) => boolean;
    onRetry?: (error: unknown, attempt: number, delayMs: number) => void;
    sleep?: (ms: number) => Promise<void>;
    random?: () => number;
}

const DEFAULTS = {
    attempts: 6,
    baseDelayMs: 600,
    maxDelayMs: Number.POSITIVE_INFINITY,
    factor: 2,
};

const defaultSleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

export function computeBackoffDelay(
    attempt: number,
    {baseDelayMs = DEFAULTS.baseDelayMs, maxDelayMs = DEFAULTS.maxDelayMs, factor = DEFAULTS.factor}: RetryOptions = {},
    random: () => number = Math.random,
): number {
    const exponential = baseDelayMs * Math.pow(factor, Math.max(0, attempt - 1));
    const capped = Math.min(exponential, maxDelayMs);
    return Math.round(capped / 2 + random() * (capped / 2));
}

export async function retryWithBackoff<T>(
    operation: () => Promise<T>,
    options: RetryOptions = {},
): Promise<T> {
    const {
        attempts = DEFAULTS.attempts,
        shouldRetry = () => true,
        onRetry,
        sleep = defaultSleep,
        random = Math.random,
    } = options;

    let lastError: unknown;

    for (let attempt = 1; attempt <= attempts; attempt++) {
        try {
            return await operation();
        } catch (error) {
            lastError = error;

            const isLastAttempt = attempt === attempts;
            if (isLastAttempt || !shouldRetry(error, attempt)) {
                throw error;
            }

            const delayMs = computeBackoffDelay(attempt, options, random);
            onRetry?.(error, attempt, delayMs);
            await sleep(delayMs);
        }
    }

    throw lastError;
}
